// The original transcript as readable text: caption segments joined into paragraphs, grouped by
// the video's chapters. Used by the transcript view and for "Transkript kopieren".

import type { Chapter, TranscriptSegment } from '../../shared/api';
import { formatDuration } from './format';

export interface TranscriptParagraph {
  /** Offset in seconds of the paragraph's first segment. */
  start: number;
  text: string;
}

export interface TranscriptSection {
  /** Chapter title; undefined for a video without chapters (or the part before the first one). */
  title?: string;
  start: number;
  paragraphs: TranscriptParagraph[];
}

/** A paragraph ends at the first sentence end after this many characters … */
const SOFT_LIMIT = 450;
/** … or here at the latest (auto-generated captions have no punctuation). */
const HARD_LIMIT = 900;
const SENTENCE_END = /[.!?…]["'»«“”)\]]*$/;

export function transcriptSections(transcript: {
  segments: TranscriptSegment[];
  chapters?: Chapter[] | undefined;
}): TranscriptSection[] {
  const chapters = [...(transcript.chapters ?? [])].sort((a, b) => a.start - b.start);
  const sections: TranscriptSection[] = [];
  let section: TranscriptSection | undefined;
  let paragraph: TranscriptParagraph | undefined;
  let nextChapter = 0;

  for (const segment of transcript.segments) {
    const text = segment.text.replace(/\s+/g, ' ').trim();
    if (!text) continue;
    // Caption cues may start a little before the chapter mark.
    let chapter: Chapter | undefined;
    while (nextChapter < chapters.length && chapters[nextChapter]!.start <= segment.start + 1) chapter = chapters[nextChapter++];
    if (chapter || !section) {
      section = {
        ...(chapter ? { title: chapter.title } : {}),
        start: chapter ? Math.min(chapter.start, segment.start) : segment.start,
        paragraphs: [],
      };
      sections.push(section);
      paragraph = undefined;
    }
    if (
      paragraph &&
      (paragraph.text.length >= HARD_LIMIT || (paragraph.text.length >= SOFT_LIMIT && SENTENCE_END.test(paragraph.text)))
    ) {
      paragraph = undefined;
    }
    if (paragraph) {
      paragraph.text += ` ${text}`;
    } else {
      paragraph = { start: segment.start, text };
      section.paragraphs.push(paragraph);
    }
  }
  return sections;
}

/**
 * Plain text for the clipboard: chapter titles on their own line, paragraphs separated by blank
 * lines; with `timestamps`, every paragraph (and chapter) starts with "[m:ss]".
 */
export function transcriptPlainText(sections: TranscriptSection[], options: { timestamps: boolean }): string {
  const stamp = (seconds: number) => (options.timestamps ? `[${formatDuration(seconds)}] ` : '');
  const blocks: string[] = [];
  for (const section of sections) {
    if (section.title) blocks.push(`${stamp(section.start)}${section.title}`);
    for (const paragraph of section.paragraphs) blocks.push(`${stamp(paragraph.start)}${paragraph.text}`);
  }
  return blocks.join('\n\n');
}
